import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";

export default function RuptureStock() {
    const navigate = useNavigate();
    const [produits, setProduits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterCategorie, setFilterCategorie] = useState('');
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetchProduits();
        fetchCategories();
    }, [filterCategorie]);

    const fetchProduits = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/stock_alertes.php?action=rupture';
            
            const params = new URLSearchParams();
            if (filterCategorie) params.append('categorie', filterCategorie);
            if (params.toString()) url += '&' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduits(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stock_alertes.php?action=categories',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur catégories", err);
        }
    };

    const filteredProduits = produits.filter(p => 
        p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
        p.designation?.toLowerCase().includes(filterText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="danger" />
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <>
            {/* Filtres */}
            <Row className="g-3 mb-4">
                <Col md={5}>
                    <InputGroup>
                        <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                        <Form.Control
                            placeholder="Rechercher un produit..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </InputGroup>
                </Col>
                <Col md={4}>
                    <Form.Select 
                        value={filterCategorie}
                        onChange={(e) => setFilterCategorie(e.target.value)}
                    >
                        <option value="">Toutes catégories</option>
                        {categories.map(c => (
                            <option key={c.id_categorie} value={c.id_categorie}>
                                {c.nom_categorie}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={3}>
                    <Button variant="outline-secondary" onClick={() => {
                        setFilterText('');
                        setFilterCategorie('');
                    }} className="w-100">
                        <i className="ri-refresh-line me-2"></i>Réinitialiser
                    </Button>
                </Col>
            </Row>

            {/* Tableau */}
            <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                    <thead className="bg-light">
                        <tr>
                            <th>Réf.</th>
                            <th>Produit</th>
                            <th>Catégorie</th>
                            <th className="text-center">Dernier stock</th>
                            <th className="text-center">Jrs sans vente</th>
                            <th>Prix achat</th>
                            <th>Prix vente</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProduits.length > 0 ? (
                            filteredProduits.map((p, index) => (
                                <tr key={p.id_produit} style={{
                                    animation: `fadeIn 0.3s ease ${index * 0.03}s both`,
                                    backgroundColor: p.jours_sans_vente > 30 ? 'rgba(220, 53, 69, 0.05)' : 'transparent'
                                }}>
                                    <td>
                                        <Badge bg="light" text="dark">{p.reference}</Badge>
                                    </td>
                                    <td>
                                        <Link to={`/stock/produits/${p.id_produit}`} className="text-primary">
                                            {p.designation}
                                        </Link>
                                    </td>
                                    <td>{p.nom_categorie || '-'}</td>
                                    <td className="text-center">
                                        <Badge bg="danger" className="px-3 py-2">
                                            {p.quantite_reelle || 0}
                                        </Badge>
                                    </td>
                                    <td className="text-center">
                                        <span className={p.jours_sans_vente > 30 ? 'text-danger fw-bold' : ''}>
                                            {p.jours_sans_vente || 0} jours
                                        </span>
                                    </td>
                                    <td>{formatCurrency(p.prix_achat_ht)}</td>
                                    <td>{formatCurrency(p.prix_vente_ht)}</td>
                                    <td className="text-center">
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Réapprovisionner</Tooltip>}>
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm"
                                                className="rounded-circle me-2"
                                                style={{ width: '32px', height: '32px' }}
                                                onClick={() => navigate(`/stock/mouvements/ajouter/${p.id_produit}?type=entree`)}
                                            >
                                                <i className="ri-add-line"></i>
                                            </Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Voir historique</Tooltip>}>
                                            <Button 
                                                variant="outline-info" 
                                                size="sm"
                                                className="rounded-circle"
                                                style={{ width: '32px', height: '32px' }}
                                                onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                            >
                                                <i className="ri-eye-line"></i>
                                            </Button>
                                        </OverlayTrigger>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-center py-5">
                                    <i className="ri-checkbox-circle-line fs-1 text-success mb-3"></i>
                                    <h5>Aucun produit en rupture</h5>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </>
    );
}